import {
  usePaginatedResource,
  useResourceItem,
  useCreateResource,
  useEditResource,
  useDeleteResource,
} from "./useCrudResource";
import {
  getCustomerDepartments,
  getCustomerDepartment,
  createCustomerDepartment,
  editCustomerDepartment,
  deleteCustomerDepartment,
  getAllCustomerDepartments,
  getTrashedCustomerDepartments,
  restoreCustomerDepartment,
} from "api/customerDepartmentApi";
import { useMutation, useQueryClient } from "@tanstack/react-query";

export const CUSTOMER_DEPARTMENT_QUERY_KEY = "customer-departments"
export const CUSTOMER_DEPARTMENT_GROUP_QUERY_KEY = "customer-departments-group"

// -------------------- FETCH PAGINATED --------------------
export const useCustomerDepartments = (params = {}, enabled = true) => {
  return usePaginatedResource(
    CUSTOMER_DEPARTMENT_QUERY_KEY,
    getCustomerDepartments,
    params,
    enabled
  )
}

// -------------------- FETCH SINGLE ITEM --------------------
export const useCustomerDepartment = (id, enabled = true) => {
  return useResourceItem(
    CUSTOMER_DEPARTMENT_QUERY_KEY,
    getCustomerDepartment,
    id,
    enabled
  )
}

// -------------------- CREATE --------------------
export const useCreateCustomerDepartment = () => {
  return useCreateResource(
    createCustomerDepartment,
    CUSTOMER_DEPARTMENT_QUERY_KEY
  )
}

// -------------------- EDIT --------------------
export const useEditCustomerDepartment = () => {
  return useEditResource(
    editCustomerDepartment,
    CUSTOMER_DEPARTMENT_QUERY_KEY
  )
}

// -------------------- DELETE --------------------
export const useDeleteCustomerDepartment = () => {
  return useDeleteResource(
    deleteCustomerDepartment,
    CUSTOMER_DEPARTMENT_QUERY_KEY
  )
}

// -------------------- ADDITIONAL --------------------

// Get all customer departments without pagination
export const useAllCustomerDepartments = (params = {}) => {
  return usePaginatedResource(
    CUSTOMER_DEPARTMENT_QUERY_KEY,
    getAllCustomerDepartments,
    { ...params, all: true },
    true
  )
}

// Get trashed customer departments
export const useTrashedCustomerDepartments = (params = {}) => {
  return usePaginatedResource(
    CUSTOMER_DEPARTMENT_QUERY_KEY,
    getTrashedCustomerDepartments,
    params,
    true
  )
}

// Restore a trashed customer department
export const useRestoreCustomerDepartment = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: restoreCustomerDepartment,
    onSuccess: (res, { id }) => {
      const restoredItem = res?.data
      if (!restoredItem) return

      // Update paginated cache
      queryClient.setQueriesData([CUSTOMER_DEPARTMENT_QUERY_KEY, "all"], old => {
        if (!old || !old.data || !Array.isArray(old.data.data)) return old
        const updated = structuredClone(old)
        updated.data.data = [restoredItem, ...updated.data.data]
        updated.data.total = (updated.data.total || 0) + 1
        return updated
      })

      // Update individual item cache
      queryClient.setQueryData([CUSTOMER_DEPARTMENT_QUERY_KEY, id], restoredItem)
    },
  })
}
